import { Users, Lightbulb, Target, Award } from "lucide-react";
import Footer from "@/components/Footer";

const IMAGES = {
  team: "https://images.pexels.com/photos/5532988/pexels-photo-5532988.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lab: "https://images.pexels.com/photos/15831822/pexels-photo-15831822.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const values = [
  {
    icon: Lightbulb,
    title: "Innovación",
    description: "Investigamos constantemente nuevas fórmulas, ingredientes y procesos para estar a la vanguardia.",
  },
  {
    icon: Target,
    title: "Precisión",
    description: "Cada fórmula es desarrollada con rigor científico para garantizar consistencia y calidad.",
  },
  {
    icon: Users,
    title: "Colaboración",
    description: "Trabajamos codo a codo con nuestros clientes para materializar su visión de producto.",
  },
  {
    icon: Award,
    title: "Excelencia",
    description: "Nos comprometemos con los más altos estándares en cada fase del proceso productivo.",
  },
];

const team = [
  { name: "Equipo de I+D", role: "Formuladores y químicos especializados en bebidas" },
  { name: "Producción", role: "Ingenieros industriales y técnicos de planta" },
  { name: "Calidad", role: "Analistas y responsables de control de calidad" },
  { name: "Comercial", role: "Especialistas en desarrollo de negocio B2B" },
];

export default function AboutPage() {
  return (
    <main className="min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.lab} alt="Lab" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-udl-bg via-udl-bg/95 to-udl-bg/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-4 block">
            Sobre Nosotros
          </span>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-udl-white mb-6 max-w-3xl">
            Ciencia + Industria = <span className="text-udl-primary">Tu Bebida</span>
          </h1>
          <p className="text-udl-gray-400 text-lg max-w-2xl leading-relaxed">
            Unique Drinks Lab nace como laboratorio de innovación dentro de una fábrica real de bebidas. 
            Somos científicos, ingenieros y especialistas en producción.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-32 bg-udl-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-4 block">
                Nuestra Misión
              </span>
              <h2 className="font-heading text-4xl font-bold text-udl-white mb-6">
                Convertir ideas en bebidas fabricables
              </h2>
              <p className="text-udl-gray-400 text-lg leading-relaxed mb-6">
                Nuestra misión es convertir ideas en bebidas fabricables, estables y escalables. 
                Combinamos conocimiento científico con capacidad industrial para hacer realidad 
                cualquier concepto de bebida.
              </p>
              <p className="text-udl-gray-400 leading-relaxed">
                No somos intermediarios ni consultores externos. Controlamos todo el proceso: 
                desde la formulación hasta la producción final, pasando por pruebas piloto, 
                embotellado y control de calidad.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden">
                <img src={IMAGES.team} alt="Team" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-udl-primary p-6">
                <p className="text-udl-bg font-heading font-bold">From Formula</p>
                <p className="text-udl-bg font-heading font-bold">to Factory</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-4 block">
              Valores
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-udl-white">
              Nuestro ADN
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div key={i} className="card-feature text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-udl-primary/10 flex items-center justify-center">
                  <value.icon className="text-udl-primary" size={28} />
                </div>
                <h3 className="text-udl-white font-heading font-bold text-lg mb-3">{value.title}</h3>
                <p className="text-udl-gray-400 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-32 bg-udl-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-4 block">
              Equipo
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-udl-white">
              Profesionales especializados
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, i) => (
              <div key={i} className="bg-udl-card border border-udl-gray-800 p-6">
                <div className="w-12 h-12 bg-udl-surface mb-4 flex items-center justify-center text-udl-primary font-heading font-bold">
                  {member.name.charAt(0)}
                </div>
                <h3 className="text-udl-white font-heading font-bold mb-2">{member.name}</h3>
                <p className="text-udl-gray-400 text-sm">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
